import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import type { iframeResizer } from '@iframe-resizer/child';

const VARIAVEL_TOPO = '--viewport-visivel-topo';
const VARIAVEL_ALTURA = '--viewport-visivel-altura';

/**
 * Publica em variáveis CSS do `:root` a faixa do iframe que o usuário está vendo na página pai.
 *
 * O iframe-resizer estica o iframe até a altura do conteúdo, então quem rola é o pai e a
 * viewport do documento filho é o iframe inteiro — `position: fixed` + centralização colocaria
 * o modal no meio do iframe, possivelmente fora da tela. Os modais usam essas variáveis (ver
 * `VivestModalAreaVisivelDirective`) para se posicionar na área realmente visível.
 *
 * Os dados vêm do `getParentProps` do iframe-resizer (postMessage), que o pai reenvia a cada
 * scroll/resize — funciona entre origens diferentes, sem acessar `window.parent.document`.
 */
@Injectable({
  providedIn: 'root',
})
export class ViewportVisivelService {
  private readonly rootStyle = inject(DOCUMENT).documentElement.style;

  iniciar(): void {
    if (window.parentIFrame) {
      this.acompanharPaginaPai();
      return;
    }

    // Fora de um iframe o onReady nunca dispara e os modais usam a viewport do próprio documento.
    window.iFrameResizer = {
      ...window.iFrameResizer,
      onReady: () => this.acompanharPaginaPai(),
    };
  }

  private acompanharPaginaPai(): void {
    window.parentIFrame.getParentProps((props) => this.publicarAreaVisivel(props));
  }

  // iframe vem de getBoundingClientRect() no pai e viewport do visualViewport: ambos no mesmo
  // referencial, então a diferença dá a faixa visível em coordenadas do documento filho.
  private publicarAreaVisivel({ iframe, viewport }: iframeResizer.ParentProps): void {
    const topoVisivel = Math.max(0, viewport.offsetTop - iframe.top);
    const baseVisivel = Math.min(iframe.height, viewport.offsetTop + viewport.height - iframe.top);
    const alturaVisivel = Math.max(0, baseVisivel - topoVisivel);

    this.rootStyle.setProperty(VARIAVEL_TOPO, `${topoVisivel}px`);
    this.rootStyle.setProperty(VARIAVEL_ALTURA, `${alturaVisivel}px`);
  }
}
